import React from 'react';
import { Phone, CloudRain, ArrowRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { isFallPrepSeason } from '../utils/season';
const PHONE_NUMBER = '(206) 650-0129';

/**
 * Top announcement bar.
 *
 * Two static halves, no rotation: an always-true ordering message on the left
 * and the full phone number on the right, on every screen size.
 *
 * It previously carried a "live order ticker" — a stream of orders generated
 * on a timer in utils/liveFeed.ts. Against competitors publishing verified
 * delivery counts, invented activity in the site's most prominent fixed slot
 * is a liability rather than proof, so it is gone. "Order materials online
 * 24/7" is true, is something the local yards genuinely cannot say, and holds
 * still long enough to be read.
 *
 * Losing the ticker also removes the reason the bar needed a rotation timer,
 * which is what was crowding mobile: the phone number can now be a tappable
 * number instead of a bare handset icon, which is what a contractor standing
 * in a driveway actually wants.
 */
export function AnnouncementBar() {
  const isFall = isFallPrepSeason();
  return <div className="relative z-[60] px-4 py-1.5 text-white dark:bg-[#2E2121] bg-[#342B2B]" style={{
    backgroundColor: "#202720",
    backgroundImage: "none"
  }} data-id="element-197" data-mp="a7r2my03fycl">
      <div className="mx-auto flex max-w-7xl items-center gap-2 sm:gap-3" data-id="element-198" data-mp="1a8cxgbi8vud">
        <span className="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap text-[12px] font-medium tracking-tight text-white/75 md:text-sm" style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif"
      }} data-id="element-199" data-mp="oyzuldrgxvsw">
          <Clock className="h-3.5 w-3.5 shrink-0 text-[#3BE07A] md:h-4 md:w-4" aria-hidden="true" data-id="element-200" data-mp="f7wykqdec70h" />
          <span className="truncate" data-id="element-201" data-mp="k6zt3gvr2uu7">Order materials online 24/7</span>
        </span>

        {/* Seasonal campaign link. Desktop only — on a phone the bar's single
            line is already spent on the ordering message and the phone number,
            both of which outrank a campaign. Mobile reaches the same page from
            the hero and the nav menu. */}
        {isFall && <Link to="/fall-prep" className="ml-auto hidden shrink-0 items-center gap-1.5 whitespace-nowrap border-l border-white/15 pl-4 text-[13px] font-semibold tracking-tight text-[#3BE07A] transition-colors hover:text-white sm:inline-flex" data-id="element-202" data-mp="1hkpzqu1a09v8">
            <CloudRain className="h-3.5 w-3.5" aria-hidden="true" data-id="element-203" data-mp="18k9x1mscx6rf" />
            Fix it before the rain
            <ArrowRight className="h-3 w-3 shrink-0" aria-hidden="true" data-id="element-204" data-mp="13mrmxt2lslhu" />
          </Link>}

        {/* Full number on every screen, not just desktop. It is both the
            fallback for anyone who would rather talk to a person and a trust
            signal — a real local number, visible without tapping anything. */}
        <a href="tel:206-650-0129" aria-label={`Call ${PHONE_NUMBER}`} className={`inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap text-[12px] font-medium tracking-tight text-white/75 transition-colors hover:text-white md:pl-4 md:text-sm ${isFall ? 'ml-auto sm:ml-4 sm:border-l sm:border-white/15' : 'ml-auto border-l border-white/15 pl-3'}`} style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif"
      }} data-id="element-205" data-mp="oupqkizqsamk">
          <Phone className="h-4 w-4 shrink-0 text-[#3BE07A]" aria-hidden="true" data-id="element-206" data-mp="30jnuxbbav1ji" />
          <span data-id="element-207" data-mp="35iqnvxe09hb4">{PHONE_NUMBER}</span>
        </a>
      </div>
    </div>;
}